<template>
  <EntityListDisplay
      :items="groups"
      :columns="groupColumns"
      :hasActions="true"
  >
    <template #actions="{ item }">
      <div class="btn-group pt-3" role="group">
        <button v-if="canViewGroup" class="btn btn-success p-1 ms-1 shadow-3d" @click="viewGroup(item)" title="Ver detalles del rol"><i class="material-icons">visibility</i></button>
        <button v-if="canEditGroup && !isAdministratorRole(item)" class="btn btn-info p-1 ms-1 shadow-3d" @click="editGroup(item)" title="Editar rol"><i class="material-icons">edit</i></button>
        <button v-if="canEditGroup && !isAdministratorRole(item)" class="btn btn-warning p-1 ms-1 shadow-3d" @click="openAssignPermModal(item)" title="Asignar permisos"><i class="material-icons">vpn_key</i></button>
        <button v-if="canDeleteGroup && !isAdministratorRole(item)" class="btn btn-danger p-1 ms-1 shadow-3d" @click="deleteGroup(item)" title="Eliminar rol"><i class="material-icons">delete</i></button>
        <span v-if="isAdministratorRole(item)" class="badge bg-primary ms-2" title="El rol Administrador no puede ser modificado ni eliminado">
          <i class="material-icons" style="font-size: 14px; vertical-align: middle;">lock</i>
          Protegido
        </span>
      </div>
    </template>
    <template #card="{ item }">
      <div class="user-card-header">
        <div class="avatar-3d">
          <i class="material-icons">group</i>
        </div>
        <div class="user-card-title">
          <span class="user-name">{{ item.name }}</span>
        </div>
      </div>
      <div class="user-card-body">
        <div class="user-info"><i class="material-icons">security</i> Permisos:
          {{ (item.permission && Array.isArray(item.permission)) ? item.permission.length : 0 }}
        </div>
      </div>
    </template>
    <template #new>
      <div class="d-flex gap-2 align-items-center">
        <button v-if="canSyncPermissionsAndRoles" class="btn btn-warning btn-new-user d-flex align-items-center gap-2" @click="syncPermissionsAndRoles" :disabled="syncing">
          <i class="material-icons">{{ syncing ? 'sync' : 'sync_alt' }}</i>
          <span>{{ syncing ? 'Sincronizando...' : 'Sincronizar Permisos y Roles' }}</span>
        </button>
        <button class="btn btn-success btn-new-user d-flex align-items-center gap-2" @click="showNewGroupModal = true">
          <i class="material-icons">group_add</i>
          <span>Nuevo Rol</span>
        </button>
      </div>
    </template>
    <template #title>
  Lista de Roles
    </template>
  </EntityListDisplay>
  <!-- Modal de nuevo grupo -->
  <div v-if="showNewGroupModal" class="modal show">
    <div class="modal-content modal-professional">
      <div class="modal-header">
        <h4 class="modal-title">
          <i class="material-icons">group_add</i>
          Crear Nuevo Rol
        </h4>
        <span class="modal-close" @click="showNewGroupModal=false">
          <i class="material-icons">close</i>
        </span>
      </div>
      <form @submit.prevent="createGroup" class="modal-form">
        <div class="form-group">
          <label class="form-label">
            <i class="material-icons">group</i>
            Nombre del Rol *
          </label>
          <input 
            v-model="newGroup.name" 
            class="form-control form-control-modern" 
            placeholder="Ingrese el nombre del rol"
            required
            @blur="validateGroupName"
          />
          <div v-if="groupErrors.name" class="error-message">
            <i class="material-icons">error</i>
            {{ groupErrors.name }}
          </div>
        </div>
        
        <div class="modal-actions">
          <button type="button" class="btn btn-secondary" @click="showNewGroupModal=false">
            <i class="material-icons">cancel</i>
            Cancelar
          </button>
          <button type="submit" class="btn btn-primary" :disabled="!isGroupFormValid">
            <i class="material-icons">save</i>
            Crear Rol
          </button>
        </div>
      </form>
    </div>
  </div>
  <!-- Modal de detalle -->
  <DetailModal
    :show="showGroupModal"
    title="Detalles de Rol"
    subtitle="Información completa del rol y sus permisos"
    icon="group"
    :data="selectedGroup"
    size="large"
    :show-edit-button="true"
    :hidden-fields="['id', 'createdAt', 'updatedAt']"
    :field-labels="{
      name: 'Nombre del Rol',
      permission: 'Permisos Asignados'
    }"
    :field-icons="{
      name: 'group',
      permission: 'security'
    }"
    @close="showGroupModal = false"
    @edit="editGroup(selectedGroup)"
  >
    <template #content>
      <div v-if="selectedGroup" class="group-details">
        <!-- Información del Rol -->
        <div class="detail-section">
          <h6 class="section-title">
            <i class="material-icons">group</i>
            Información del Rol
          </h6>
          <div class="data-display">
            <div class="data-row">
              <div class="data-label">
                <i class="material-icons">group</i>
                Nombre del Rol
              </div>
              <div class="data-value">{{ selectedGroup.name }}</div>
            </div>
            <div class="data-row">
              <div class="data-label">
                <i class="material-icons">security</i>
                Total de Permisos
              </div>
              <div class="data-value value-number">
                {{ (selectedGroup.permission && Array.isArray(selectedGroup.permission)) ? selectedGroup.permission.length : 0 }}
              </div>
            </div>
          </div>
        </div>

        <!-- Lista de Permisos -->
        <div class="detail-section" v-if="selectedGroup.permission && Array.isArray(selectedGroup.permission) && selectedGroup.permission.length > 0">
          <h6 class="section-title">
            <i class="material-icons">security</i>
            Permisos Asignados
          </h6>
          <div class="permissions-list">
            <div 
              v-for="(permission, index) in selectedGroup.permission" 
              :key="permission.id || index"
              class="permission-item"
            >
              <div class="permission-content">
                <div class="permission-name">
                  <i class="material-icons">vpn_key</i>
                  {{ permission.name || permission.description || 'Permiso sin nombre' }}
                </div>
                <div class="permission-description" v-if="permission.description && permission.description !== permission.name">
                  {{ permission.description }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sin permisos -->
        <div class="detail-section" v-else>
          <h6 class="section-title">
            <i class="material-icons">security</i>
            Permisos Asignados
          </h6>
          <div class="no-permissions">
            <i class="material-icons">info</i>
            <p>Este rol no tiene permisos asignados</p>
          </div>
        </div>
      </div>
    </template>
  </DetailModal>
  <!-- Modal de edición -->
  <div v-if="showEditModal" class="modal show">
    <div class="modal-content modal-professional">
      <div class="modal-header">
        <h4 class="modal-title">
          <i class="material-icons">edit</i>
          Editar Rol
        </h4>
        <span class="modal-close" @click="showEditModal=false">
          <i class="material-icons">close</i>
        </span>
      </div>
      <form @submit.prevent="saveEdit" class="modal-form">
        <div class="form-group">
          <label class="form-label">
            <i class="material-icons">group</i>
            Nombre del Rol *
          </label>
          <input 
            v-model="selectedGroup.name" 
            class="form-control form-control-modern" 
            placeholder="Ingrese el nombre del rol"
            required
          />
        </div>
        
        <div class="modal-actions">
          <button type="button" class="btn btn-secondary" @click="showEditModal=false">
            <i class="material-icons">cancel</i>
            Cancelar
          </button>
          <button type="submit" class="btn btn-primary">
            <i class="material-icons">save</i>
            Guardar Cambios
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Modal asignar permisos avanzado -->
  <div v-if="showAssignPermModal" class="modal show modal-assign-perms-root">
    <div ref="assignPermModalPanel" class="modal-content modal-assign-perms-content">
      <span class="modal-icon" @click="closeAssignPermModal">&times;</span>
      <h4>Asignar Permisos a Grupo</h4>
      <p class="modal-perms-hint">Pase el cursor sobre un permiso para ver el texto completo. Clic para seleccionar varios.</p>
      <div
        v-show="permFlyout.visible"
        class="modal-perms-flyout-global"
        :style="permFlyout.style"
        role="tooltip"
      >
        {{ permFlyout.text }}
      </div>
      <div class="modal-perms-row">
        <div class="modal-perms-col">
          <label class="modal-perms-label">Disponibles</label>
          <div class="modal-perms-list" role="listbox" aria-multiselectable="true">
            <template v-if="availablePerms.length">
              <div
                v-for="perm in availablePerms"
                :key="'av-' + perm.id"
                role="option"
                class="modal-perms-list-item"
                :class="{ 'is-selected': selectedAvailablePerms.includes(perm.id) }"
                :aria-selected="selectedAvailablePerms.includes(perm.id)"
                :title="permissionLabel(perm)"
                @mouseenter="showPermFlyout($event, permissionLabel(perm))"
                @mousemove="movePermFlyout($event)"
                @mouseleave="hidePermFlyout"
                @click="toggleAvailableSelection(perm.id)"
              >
                <span class="modal-perms-list-item-text">{{ permissionLabel(perm) }}</span>
              </div>
            </template>
            <div v-else class="modal-perms-list-empty">No hay permisos disponibles</div>
          </div>
        </div>
        <div class="modal-perms-actions">
          <button type="button" class="modal-perms-btn btn-success" @click="assignSelectedPerms" title="Asignar seleccionados"><i class="material-icons">arrow_forward</i></button>
          <button type="button" class="modal-perms-btn btn-success" @click="assignAllPerms" title="Asignar todos"><i class="material-icons">double_arrow</i></button>
          <button type="button" class="modal-perms-btn btn-danger" @click="removeSelectedPerms" title="Quitar seleccionados"><i class="material-icons">arrow_back</i></button>
          <button type="button" class="modal-perms-btn btn-danger" @click="removeAllPerms" title="Quitar todos"><i class="material-icons">keyboard_double_arrow_left</i></button>
        </div>
        <div class="modal-perms-col">
          <label class="modal-perms-label">Asignados</label>
          <div class="modal-perms-list" role="listbox" aria-multiselectable="true">
            <template v-if="assignedPerms.length">
              <div
                v-for="perm in assignedPerms"
                :key="'as-' + perm.id"
                role="option"
                class="modal-perms-list-item"
                :class="{ 'is-selected': selectedAssignedPerms.includes(perm.id) }"
                :aria-selected="selectedAssignedPerms.includes(perm.id)"
                :title="permissionLabel(perm)"
                @mouseenter="showPermFlyout($event, permissionLabel(perm))"
                @mousemove="movePermFlyout($event)"
                @mouseleave="hidePermFlyout"
                @click="toggleAssignedSelection(perm.id)"
              >
                <span class="modal-perms-list-item-text">{{ permissionLabel(perm) }}</span>
              </div>
            </template>
            <div v-else class="modal-perms-list-empty">No hay permisos asignados</div>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-end mt-3">
        <button type="button" class="btn btn-warning" @click="assignPerms">Guardar cambios</button>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import EntityListDisplay from "@/components/EntityListDisplay.vue";
import DetailModal from "@/components/DetailModal.vue";
import Swal from "sweetalert2";
import {showSuccess, showError, syncPermissionsAndRoles, me} from "@/actions";
import {getGraphQLError, formatPermissionError} from "@/utils/graphqlErrorHandler";
import {Permissions} from '@/utils/permissions';

export default {
  name: "List_Groups",
  components: {EntityListDisplay, DetailModal},
  data() {
    return {
      groupColumns: [
        {key: "name", label: "Nombre"}
      ],
      groups: [],
      permissions: [],
      selectedGroup: {
        id: "",
        name: "",
      },
      showGroupModal: false,
      showEditModal: false,
      showNewGroupModal: false,
      showAssignPermModal: false,
      assignPermIds: [],
      assignPermGroupId: null,
      selectedAvailablePerms: [],
      selectedAssignedPerms: [],
      permFlyout: {
        visible: false,
        text: '',
        style: {},
      },
      newGroup: {
        name: ""
      },
      groupErrors: {},
      syncing: false,
      currentUserEmail: ""
    }
  },
  computed: {
    ...mapGetters(['isAdmin', 'hasPermission', 'canDelete']),
    canViewGroup() {
      // Administrador siempre puede ver, o si tiene el permiso
      return this.isAdmin || this.hasPermission(Permissions.SECURITY_ROLE_CRUD);
    },
    canEditGroup() {
      // Administrador siempre puede editar, o si tiene el permiso
      return this.isAdmin || this.hasPermission(Permissions.SECURITY_ROLE_CRUD);
    },
    canDeleteGroup() {
      // Administrador siempre puede eliminar, o si tiene el permiso
      return this.isAdmin || this.hasPermission(Permissions.SECURITY_ROLE_DELETE);
    },
    isGroupFormValid() {
      return this.newGroup.name && 
             this.newGroup.name.trim().length >= 2 &&
             !this.groupErrors.name;
    },
    availablePerms() {
      const perms = Array.isArray(this.permissions) ? this.permissions : [];
      const assigned = Array.isArray(this.assignPermIds) ? this.assignPermIds : [];
      return perms.filter(p => !assigned.includes(p.id));
    },
    assignedPerms() {
      const perms = Array.isArray(this.permissions) ? this.permissions : [];
      const assigned = Array.isArray(this.assignPermIds) ? this.assignPermIds : [];
      return perms.filter(p => assigned.includes(p.id));
    },
    canSyncPermissionsAndRoles() {
      return this.currentUserEmail === 'admin@gmail.com';
    }
  },
  methods: {
    async loadCurrentUser() {
      try {
        const response = await me();
        const currentUser = response?.data?.data?.me || response?.data?.me;
        this.currentUserEmail = (currentUser?.email || '').trim().toLowerCase();
      } catch (error) {
        console.error('Error cargando usuario actual:', error);
        this.currentUserEmail = '';
      }
    },
    async syncPermissionsAndRoles() {
      if (!this.canSyncPermissionsAndRoles) {
        showError("Solo el usuario admin@gmail.com puede sincronizar permisos y roles.", "Acceso denegado");
        return;
      }

      const confirmed = await Swal.fire({
        title: '¿Sincronizar Permisos y Roles?',
        html: `
          <p>Esta acción sincronizará:</p>
          <ul style="text-align: left; display: inline-block;">
            <li><strong>Permisos:</strong> Desde el código a la base de datos (siempre se actualizan)</li>
            <li><strong>Roles por defecto:</strong> Solo creará roles si no existen (Administrador, Supervisor, Agente)</li>
            <li><strong>Permisos de roles:</strong> Solo se asignan a roles nuevos. Los roles existentes NO se modifican.</li>
          </ul>
          <p style="margin-top: 15px; color: #ff9800;"><strong>Nota:</strong> Los permisos de roles existentes se preservan. Puedes modificarlos desde la interfaz sin que la sincronización los sobrescriba.</p>
          <p style="margin-top: 10px; color: #d33;"><strong>¿Deseas continuar?</strong></p>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, sincronizar',
        cancelButtonText: 'Cancelar',
        reverseButtons: true
      });

      if (!confirmed.isConfirmed) {
        return;
      }

      this.syncing = true;
      try {
        const response = await syncPermissionsAndRoles({
          syncPermissions: true,
          syncRoles: true,
          forceRoles: false  // NO sobrescribir permisos de roles existentes
        });

        if (response.data.data.syncPermissionsAndRoles.success) {
          const result = response.data.data.syncPermissionsAndRoles;
          const message = `
            <div style="text-align: left;">
              <p><strong>Sincronización completada exitosamente</strong></p>
              <hr>
              <p><strong>Permisos:</strong></p>
              <ul>
                <li>Creados: ${result.permissionsCreated || 0}</li>
                <li>Actualizados: ${result.permissionsUpdated || 0}</li>
                <li>Sin cambios: ${result.permissionsSkipped || 0}</li>
              </ul>
              <p><strong>Roles:</strong></p>
              <ul>
                <li>Creados: ${result.rolesCreated || 0}</li>
                <li>Actualizados: ${result.rolesUpdated || 0}</li>
              </ul>
            </div>
          `;
          
          await Swal.fire({
            title: '¡Sincronización Exitosa!',
            html: message,
            icon: 'success',
            confirmButtonText: 'Aceptar'
          });

          // Recargar grupos y permisos
          await this.fetchGroups();
        } else {
          const graphQLError = getGraphQLError(response, 'syncPermissionsAndRoles');
          const error = graphQLError || response.data.data.syncPermissionsAndRoles?.error || 'Error desconocido';
          const formattedError = formatPermissionError(error);
          await Swal.fire({
            title: 'Error en la Sincronización',
            html: formattedError.replace(/\n/g, '<br>'),
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
        }
      } catch (error) {
        console.error('Error al sincronizar:', error);
        const errorMessage = error.response?.data?.errors?.[0]?.message || error.message || 'Ocurrió un error al sincronizar permisos y roles';
        const formattedError = formatPermissionError(errorMessage);
        await Swal.fire({
          title: 'Error',
          html: formattedError.replace(/\n/g, '<br>'),
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
      } finally {
        this.syncing = false;
      }
    },
    async fetchGroups() {
      const {groups, permissions} = await import("@/actions");
      const resGroups = await groups();
      // Asegurar que cada grupo tenga la propiedad permission inicializada
      this.groups = (resGroups.data.data.groups || []).map(group => ({
        ...group,
        permission: group.permission || []
      }));
      const resPerms = await permissions();
      this.permissions = resPerms.data.data.permissions || [];
    },
    async openAssignPermModal(group) {
      // Proteger el rol Administrador
      if (this.isAdministratorRole(group)) {
        await Swal.fire({
          title: 'Rol Protegido',
          text: 'El rol Administrador no puede ser modificado. Este rol tiene acceso completo a todos los permisos del sistema automáticamente.',
          icon: 'warning',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        });
        return;
      }
      if (!Array.isArray(this.permissions) || this.permissions.length === 0) {
        const {permissions} = await import("@/actions");
        const resPerms = await permissions();
        this.permissions = resPerms.data.data.permissions || [];
      }
      this.assignPermGroupId = group.id;
      this.assignPermIds = (group.permission && Array.isArray(group.permission)) ? group.permission.map(p => p.id) : [];
      const perms = Array.isArray(this.permissions) ? this.permissions : [];
      const assigned = Array.isArray(this.assignPermIds) ? this.assignPermIds : [];
      this.selectedAvailablePerms = [];
      this.selectedAssignedPerms = [];
      this.hidePermFlyout();
      this.showAssignPermModal = true;
    },
    showPermFlyout(event, text) {
      this.permFlyout.text = text || '';
      this.permFlyout.visible = !!text;
      this.movePermFlyout(event);
    },
    movePermFlyout(event) {
      if (!this.permFlyout.visible || !event?.currentTarget) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const gap = 6;
      let top = rect.top - gap;
      let left = rect.left;
      const maxW = Math.min(420, window.innerWidth * 0.9);
      if (left + maxW > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - maxW - 8);
      }
      if (top < 8) {
        top = rect.bottom + gap;
        this.permFlyout.style = {
          top: `${top}px`,
          left: `${left}px`,
          maxWidth: `${maxW}px`,
          transform: 'none',
        };
        return;
      }
      this.permFlyout.style = {
        top: `${top}px`,
        left: `${left}px`,
        maxWidth: `${maxW}px`,
        transform: 'translateY(-100%)',
      };
    },
    hidePermFlyout() {
      this.permFlyout.visible = false;
      this.permFlyout.text = '';
      this.permFlyout.style = {};
    },
    closeAssignPermModal() {
      this.hidePermFlyout();
      this.showAssignPermModal = false;
    },
    permissionLabel(perm) {
      if (!perm) return 'Permiso sin nombre';
      return perm.description || perm.name || 'Permiso sin nombre';
    },
    toggleAvailableSelection(id) {
      const idx = this.selectedAvailablePerms.indexOf(id);
      if (idx >= 0) {
        this.selectedAvailablePerms.splice(idx, 1);
      } else {
        this.selectedAvailablePerms.push(id);
      }
    },
    toggleAssignedSelection(id) {
      const idx = this.selectedAssignedPerms.indexOf(id);
      if (idx >= 0) {
        this.selectedAssignedPerms.splice(idx, 1);
      } else {
        this.selectedAssignedPerms.push(id);
      }
    },
    assignSelectedPerms() {
      this.assignPermIds = [...new Set([...this.assignPermIds, ...this.selectedAvailablePerms])];
      this.selectedAvailablePerms = [];
    },
    assignAllPerms() {
      this.assignPermIds = this.permissions.map(p => p.id);
      this.selectedAvailablePerms = [];
    },
    removeSelectedPerms() {
      this.assignPermIds = this.assignPermIds.filter(id => !this.selectedAssignedPerms.includes(id));
      this.selectedAssignedPerms = [];
    },
    removeAllPerms() {
      this.assignPermIds = [];
      this.selectedAssignedPerms = [];
    },
    async assignPerms() {
      const {assignPermissionsToGroup} = await import("@/actions");
      try {
        // Enviar los IDs de los permisos actualmente asignados
        const permissionIds = this.assignedPerms.map(p => p.id);
        const res = await assignPermissionsToGroup({
          groupId: this.assignPermGroupId,
          permissionIds
        });
        
        // Verificar errores de GraphQL primero
        const graphQLError = getGraphQLError(res, 'assignPermissionsToGroup');
        if (graphQLError) {
          const formattedError = formatPermissionError(graphQLError);
          await Swal.fire({
            title: 'Error al Asignar Permisos',
            html: formattedError.replace(/\n/g, '<br>'),
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
          return;
        }
        
        if (res.data.data.assignPermissionsToGroup && res.data.data.assignPermissionsToGroup.group) {
          this.fetchGroups();
          this.closeAssignPermModal();
          showSuccess("Permisos asignados correctamente", "Permisos asignados", {timer: 1800});
        } else {
          const error = res.data.data.assignPermissionsToGroup?.error || "Error al asignar permisos";
          const formattedError = formatPermissionError(error);
          await Swal.fire({
            title: 'Error al Asignar Permisos',
            html: formattedError.replace(/\n/g, '<br>'),
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
        }
      } catch (e) {
        console.error('Error al asignar permisos:', e);
        const errorMessage = e.response?.data?.errors?.[0]?.message || e.message || "Error de conexión";
        const formattedError = formatPermissionError(errorMessage);
        await Swal.fire({
          title: 'Error',
          html: formattedError.replace(/\n/g, '<br>'),
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
      }
    },
    // Verificar si un grupo es el rol Administrador
    isAdministratorRole(group) {
      return group && (group.name === 'Administrador' || group.name === 'administrador');
    },
    editGroup(group) {
      // Proteger el rol Administrador
      if (this.isAdministratorRole(group)) {
        Swal.fire({
          title: 'Rol Protegido',
          text: 'El rol Administrador no puede ser modificado para garantizar que siempre haya un rol con acceso completo al sistema.',
          icon: 'warning',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        });
        return;
      }
      this.selectedGroup = {...group};
      this.showEditModal = true;
    },
    async saveEdit() {
      // Proteger el rol Administrador
      if (this.isAdministratorRole(this.selectedGroup)) {
        await Swal.fire({
          title: 'Rol Protegido',
          text: 'El rol Administrador no puede ser modificado para garantizar que siempre haya un rol con acceso completo al sistema.',
          icon: 'warning',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        });
        this.showEditModal = false;
        return;
      }
      const {updateGroup} = await import("@/actions");
      try {
        const res = await updateGroup({
          id: this.selectedGroup.id,
          name: this.selectedGroup.name
        });
        
        // Verificar errores de GraphQL primero
        const graphQLError = getGraphQLError(res, 'updateGroup');
        if (graphQLError) {
          const formattedError = formatPermissionError(graphQLError);
          await Swal.fire({
            title: 'Error al Actualizar Rol',
            html: formattedError.replace(/\n/g, '<br>'),
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
          return;
        }
        
        if (res.data.data.updateGroup && res.data.data.updateGroup.group) {
          this.fetchGroups();
          this.showEditModal = false;
          showSuccess("Grupo actualizado correctamente", "Grupo actualizado", {timer: 1800});
        } else {
          const error = res.data.data.updateGroup?.error || "Error al actualizar grupo";
          const formattedError = formatPermissionError(error);
          await Swal.fire({
            title: 'Error al Actualizar Rol',
            html: formattedError.replace(/\n/g, '<br>'),
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
        }
      } catch (e) {
        console.error('Error al actualizar grupo:', e);
        const errorMessage = e.response?.data?.errors?.[0]?.message || e.message || "Error de conexión";
        const formattedError = formatPermissionError(errorMessage);
        await Swal.fire({
          title: 'Error',
          html: formattedError.replace(/\n/g, '<br>'),
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
      }
    },
    viewGroup(group) {
      this.selectedGroup = group;
      this.showGroupModal = true;
    },
    async deleteGroup(group) {
      // Proteger el rol Administrador
      if (this.isAdministratorRole(group)) {
        await Swal.fire({
          title: 'Rol Protegido',
          text: 'El rol Administrador no puede ser eliminado para garantizar que siempre haya un rol con acceso completo al sistema.',
          icon: 'warning',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        });
        return;
      }
      const result = await Swal.fire({
        title: `¿Eliminar grupo '${group.name}'?`,
        text: "Esta acción no se puede deshacer.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar"
      });
      if (result.isConfirmed) {
        const {deleteGroup} = await import("@/actions");
        try {
          const res = await deleteGroup(group.id);
          
          // Verificar errores de GraphQL primero
          const graphQLError = getGraphQLError(res, 'deleteGroup');
          if (graphQLError) {
            const formattedError = formatPermissionError(graphQLError);
            await Swal.fire({
              title: 'Error al Eliminar Rol',
              html: formattedError.replace(/\n/g, '<br>'),
              icon: 'error',
              confirmButtonText: 'Aceptar'
            });
            return;
          }
          
          if (res.data.data.deleteGroup && res.data.data.deleteGroup.ok) {
            this.groups = this.groups.filter(g => g.id !== group.id);
            showSuccess("El grupo ha sido eliminado correctamente.", "Grupo eliminado", {timer: 1800});
          } else {
            const error = res.data.data.deleteGroup?.error || "No se pudo eliminar el grupo.";
            const formattedError = formatPermissionError(error);
            await Swal.fire({
              title: 'Error al Eliminar Rol',
              html: formattedError.replace(/\n/g, '<br>'),
              icon: 'error',
              confirmButtonText: 'Aceptar'
            });
          }
        } catch (e) {
          console.error('Error al eliminar grupo:', e);
          const errorMessage = e.response?.data?.errors?.[0]?.message || e.message || "Error de conexión.";
          const formattedError = formatPermissionError(errorMessage);
          await Swal.fire({
            title: 'Error',
            html: formattedError.replace(/\n/g, '<br>'),
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
        }
      }
    },
    validateGroupName() {
      this.groupErrors = { ...this.groupErrors };
      
      if (!this.newGroup.name || this.newGroup.name.trim().length < 2) {
        this.groupErrors.name = 'El nombre del rol debe tener al menos 2 caracteres';
      } else {
        delete this.groupErrors.name;
      }
    },
    
    async createGroup() {
      // Validar antes de enviar
      this.validateGroupName();
      
      if (Object.keys(this.groupErrors).length > 0) {
        showError("Por favor corrija los errores en el formulario", "Error de validación");
        return;
      }
      
      const {createGroup} = await import("@/actions");
      try {
        const res = await createGroup(this.newGroup);
        
        // Verificar errores de GraphQL primero
        const graphQLError = getGraphQLError(res, 'createGroup');
        if (graphQLError) {
          const formattedError = formatPermissionError(graphQLError);
          await Swal.fire({
            title: 'Error al Crear Rol',
            html: formattedError.replace(/\n/g, '<br>'),
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
          return;
        }
        
        if (res.data.data.createGroup && res.data.data.createGroup.group) {
          this.fetchGroups();
          this.showNewGroupModal = false;
          this.newGroup = {name: ""};
          this.groupErrors = {};
          showSuccess("Rol creado correctamente", "Rol creado", {timer: 1800});
        } else {
          const error = res.data.data.createGroup?.error || "Error al crear rol";
          const formattedError = formatPermissionError(error);
          await Swal.fire({
            title: 'Error al Crear Rol',
            html: formattedError.replace(/\n/g, '<br>'),
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
        }
      } catch (e) {
        console.error('Error al crear grupo:', e);
        const errorMessage = e.response?.data?.errors?.[0]?.message || e.message || "Error de conexión";
        const formattedError = formatPermissionError(errorMessage);
        await Swal.fire({
          title: 'Error',
          html: formattedError.replace(/\n/g, '<br>'),
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
      }
    }
  },
  async mounted() {
    await Promise.all([
      this.fetchGroups(),
      this.loadCurrentUser()
    ]);
  }
}
</script>

<style scoped>
.btn-new-user {
  font-size: 1.1rem;
  padding: 0.5rem 1.2rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(2, 33, 38, 0.08);
  background: linear-gradient(90deg, #3A9F1D 0%, #5fe939 100%);
  color: #fff;
  font-weight: bold;
  transition: background 0.2s;
}

.btn-new-user:hover {
  background: linear-gradient(90deg, #5fe939 0%, #3A9F1D 100%);
  color: #fff;
}

.modal {
  /* Spinner global */

  .site-spinner {
    width: 60px;
    height: 60px;
    border: 7px solid #e0e0e0;
    border-top: 7px solid #3A9F1D;
    border-right: 7px solid #5fe939;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 9999;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-icon {
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
}

.modal-content {
  background-color: white;
  padding: 20px;
  border-radius: 15px;
  width: 50%;
}

/* Estilos profesionales para formularios */
.modal-professional {
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  border: none;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
  margin-bottom: 25px;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.4rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.modal-title .material-icons {
  color: #3A9F1D;
  font-size: 1.6rem;
}

.modal-close {
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background-color: #f8f9fa;
  color: #dc3545;
}

.modal-close .material-icons {
  font-size: 1.4rem;
}

.modal-form {
  padding: 0;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 0.95rem;
}

.form-label .material-icons {
  font-size: 1.1rem;
  color: #3A9F1D;
}

.form-control-modern {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background-color: #fff;
}

.form-control-modern:focus {
  outline: none;
  border-color: #3A9F1D;
  box-shadow: 0 0 0 3px rgba(58, 159, 29, 0.1);
}

.form-control-modern::placeholder {
  color: #adb5bd;
  font-style: italic;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #dc3545;
  font-size: 0.85rem;
  margin-top: 6px;
  font-weight: 500;
}

.error-message .material-icons {
  font-size: 1rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
  border-top: 2px solid #f0f0f0;
  margin-top: 25px;
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, #3A9F1D 0%, #5fe939 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(58, 159, 29, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #2d7a16 0%, #4dd32a 100%);
  box-shadow: 0 6px 16px rgba(58, 159, 29, 0.4);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  background: #6c757d;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.btn-secondary {
  background: #6c757d;
  color: white;
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
}

.btn-secondary:hover {
  background: #5a6268;
  box-shadow: 0 6px 16px rgba(108, 117, 125, 0.4);
  transform: translateY(-1px);
}

.btn .material-icons {
  font-size: 1.1rem;
}

@media (max-width: 768px) {
  .modal-professional {
    width: 95%;
    margin: 10px;
  }
  
  .modal-actions {
    flex-direction: column;
  }
  
  .btn {
    justify-content: center;
  }
}

.modal {
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.modal.show {
  opacity: 1;
}

@media (max-width: 550px) {
  .modal-content {
    width: 90%;
    margin-top: 20%;
  }
}

.user-card .card-actions {
  margin-top: 1rem;
  display: flex;
  gap: 0.5rem;
}

.user-card-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.1rem;
}

.avatar-3d {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3A9F1D 0%, #5fe939 100%);
  box-shadow: 0 4px 16px #3A9F1D44, 0 1px 0 #5fe939 inset;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 2.2rem;
  margin-right: 1.1rem;
}

.user-card-title {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: bold;
  font-size: 1.18rem;
  color: #2a4d1b;
  text-shadow: 0 1px 0 #fff;
}

.user-group {
  font-size: 0.98rem;
  color: #5fe939;
  font-weight: 500;
  margin-top: 2px;
}

.user-card-body {
  margin-bottom: 1.1rem;
}

.user-info {
  display: flex;
  align-items: center;
  font-size: 1.04rem;
  color: #3A9F1D;
  margin-bottom: 0.4rem;
}

.user-info i {
  font-size: 1.1rem;
  margin-right: 0.5rem;
  color: #5fe939;
}
/* --- Modal Asignar Permisos --- */
.modal-assign-perms-root {
  z-index: 9999;
}
.modal-assign-perms-content {
  position: relative;
  z-index: 10000;
  max-width: min(920px, 96vw);
  width: 100%;
  overflow: visible;
}
.modal-perms-hint {
  margin: 0.35rem 0 0;
  font-size: 0.88rem;
  color: #5a6a5a;
  text-align: center;
}
.modal-perms-row {
  display: flex;
  gap: 2.2rem;
  align-items: flex-start;
  justify-content: center;
  margin-top: 1.2rem;
}
.modal-perms-col {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;
}
.modal-perms-label {
  font-weight: 600;
  color: #3A9F1D;
  margin-bottom: 0.5rem;
  font-size: 1.08rem;
  text-align: center;
}
.modal-perms-list {
  width: 100%;
  min-height: 260px;
  max-height: 320px;
  overflow-y: auto;
  border-radius: 8px;
  border: 1.5px solid #5fe939;
  box-shadow: 0 2px 8px rgba(60, 159, 29, 0.08);
  background: #f8fff8;
  margin-bottom: 0.5rem;
  padding: 0.35rem;
}
.modal-perms-list-item {
  position: relative;
  display: block;
  width: 100%;
  padding: 0.45rem 0.55rem;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}
.modal-perms-flyout-global {
  position: fixed;
  z-index: 10060;
  padding: 0.5rem 0.65rem;
  background: rgba(33, 33, 33, 0.96);
  color: #fff;
  font-size: 0.85rem;
  line-height: 1.35;
  border-radius: 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.28);
  pointer-events: none;
  white-space: normal;
  word-break: break-word;
  text-align: left;
}
.modal-perms-list-item:hover {
  background: rgba(95, 233, 57, 0.15);
}
.modal-perms-list-item.is-selected {
  background: rgba(58, 159, 29, 0.22);
  font-weight: 600;
}
.modal-perms-list-item-text {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.95rem;
  line-height: 1.35;
  color: #1a2e1a;
}
.modal-perms-list-empty {
  padding: 1rem 0.75rem;
  text-align: center;
  color: #6b7b6b;
  font-size: 0.9rem;
}
.modal-perms-select {
  width: 220px;
  min-height: 260px;
  max-height: 320px;
  font-size: 1rem;
  border-radius: 8px;
  border: 1.5px solid #5fe939;
  box-shadow: 0 2px 8px rgba(60,159,29,0.08);
  padding: 0.5rem;
  background: #f8fff8;
  margin-bottom: 0.5rem;
}
.modal-perms-actions {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  align-items: center;
  justify-content: center;
  margin-top: 2.5rem;
}
.modal-perms-btn {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  border-radius: 50%;
  border: none;
  box-shadow: 0 2px 8px rgba(60,159,29,0.08);
  transition: background 0.18s;
  margin: 0;
  padding: 0;
}
.modal-perms-btn.btn-success {
  background: linear-gradient(90deg, #3A9F1D 0%, #5fe939 100%);
  color: #fff;
}
.modal-perms-btn.btn-success:hover {
  background: linear-gradient(90deg, #5fe939 0%, #3A9F1D 100%);
  color: #fff;
}
.modal-perms-btn.btn-danger {
  background: linear-gradient(90deg, #d33 0%, #ff6b6b 100%);
  color: #fff;
}
.modal-perms-btn.btn-danger:hover {
  background: linear-gradient(90deg, #ff6b6b 0%, #d33 100%);
  color: #fff;
}
@media (max-width: 700px) {
  .modal-perms-row {
    flex-direction: column;
    gap: 1.2rem;
    align-items: stretch;
  }
  .modal-perms-col {
    width: 100%;
    align-items: stretch;
  }
  .modal-perms-list {
    min-height: 180px;
    max-height: 220px;
  }
  .modal-perms-select {
    width: 100%;
    min-height: 180px;
    max-height: 220px;
  }
  .modal-perms-actions {
    flex-direction: row;
    gap: 0.7rem;
    margin-top: 1.2rem;
    justify-content: center;
  }
}

/* Estilos para el modal de detalles personalizado */
.group-details {
  .detail-section {
    margin-bottom: 24px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 1rem;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e9ecef;
    
    .material-icons {
      font-size: 20px;
      color: #667eea;
    }
  }
  
  .data-display {
    .data-row {
      display: flex;
      align-items: flex-start;
      padding: 8px 0;
      border-bottom: 1px solid #f8f9fa;
      
      &:last-child {
        border-bottom: none;
      }
      
      .data-label {
        flex: 0 0 40%;
        font-weight: 600;
        color: #2c3e50;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.9rem;
        
        .material-icons {
          font-size: 16px;
          color: #6c757d;
        }
      }
      
      .data-value {
        flex: 1;
        color: #6c757d;
        font-size: 0.9rem;
        line-height: 1.4;
        word-break: break-word;
        
        &.value-number {
          color: #17a2b8;
          font-weight: 500;
        }
      }
    }
  }
  
  .permissions-list {
    display: grid;
    gap: 8px;
    max-height: 300px;
    overflow-y: auto;
    
    .permission-item {
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      padding: 12px;
      transition: all 0.2s ease;
      
      &:hover {
        background: #e9ecef;
        border-color: #667eea;
      }
      
      .permission-content {
        .permission-name {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 4px;
          
          .material-icons {
            font-size: 18px;
            color: #667eea;
          }
        }
        
        .permission-description {
          font-size: 0.85rem;
          color: #6c757d;
          margin-left: 26px;
          line-height: 1.3;
        }
      }
    }
  }
  
  .no-permissions {
    text-align: center;
    padding: 40px 20px;
    color: #6c757d;
    
    .material-icons {
      font-size: 48px;
      margin-bottom: 16px;
      opacity: 0.5;
    }
    
    p {
      margin: 0;
      font-size: 1.1rem;
    }
  }
}
</style>
